import { inject, Injectable } from '@angular/core';
import type { CollectedStatusBanners } from '@hunting-nature/Secacam/pages/cameras/components/status-banner-camera-overview/collected-status-banners.type';
import { CamerasActions } from '@hunting-nature/Secacam/pages/cameras/redux/cameras.actions';
import type { BannerConfiguration } from '@hunting-nature/Secacam/shared/components/show-banner/banner-configuration';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';
import { Store } from '@ngrx/store';

import { Severity } from '../../../../shared/enums/severity.enum';

import type { CameraDetailsStatusBannerTypes } from './camera-details-status-banner-types.type';

/*
 * Documentation: https://ventrade.atlassian.net/wiki/spaces/ZSEUni/pages/4493803543/Status-Banner+Camera
 * Design:
 *  https://www.figma.com/design/7WtpHWs7qbAJWq6L4OUQjb/Unified-App-Design-Library?node-id=4762-21896&m=dev
 *  https://www.figma.com/design/KONE8m2jowWO2zdBu0hYL0/Gallery-concept-UI-Design?node-id=20-2629&p=f&focus-id=4520-61451&view=focus&m=dev
 */
@Injectable({ providedIn: 'root' })
export class StatusBannerCameraDetailsCollectorService {
  // services
  private readonly store = inject(Store);

  // local state
  private instantModeActive: boolean = false;

  private isOwner: boolean = false;
  private isOperator: boolean = false;
  private isViewer: boolean = false;

  private addErrorLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>>): void {
    const externalPowerSourceConnected = camera.powerCordPluggedIn;

    if (!externalPowerSourceConnected && camera.power.status === 'ERROR') {
      const key = 'error-low-battery-details';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.ERROR,
          icon: 'batteries-empty',
          message: 'cameras.statusBanner.batteriesEmpty',
          cameraName: camera.name,
          postponable: true,
          showCloseButton: true,
        },
      ];
    }
  }

  private addWarningLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>>): void {
    const externalPowerSourceConnected = camera.powerCordPluggedIn;

    if (!externalPowerSourceConnected && camera.power.status === 'WARNING') {
      const key = 'warning-out-batteries-soon-details';
      cameraBanners[key] = [
        {
          id: key + '-' + camera.id,
          severity: Severity.WARNING,
          icon: 'out-batteries-soon',
          message: 'cameras.statusBanner.outBatteriesSoon',
          cameraName: camera.name,
          postponable: true,
          showCloseButton: true,
        },
      ];
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private addInfoLevelBanners(camera: Camera, cameraBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>>): void {
    if ((this.isOwner || this.isOperator) && camera.software.updateAvailable === 'RECOMMENDED') {
      const key = 'info-firmware-update-available-on-details';
      const firmwareUpdateBanner: BannerConfiguration = {
        id: key + '-' + camera.id,
        severity: Severity.INFO,
        icon: 'firmware-update-available',
        message: 'cameras.statusBanner.firmwareUpdateAvailableOnDetails',
        cameraName: camera.name,
        postponable: false,
        clickHandler: () => this.store.dispatch(CamerasActions.openFirmwareUpdateModal()),
        showCloseButton: false,
        actionHandler: () => this.store.dispatch(CamerasActions.openFirmwareUpdateModal()),
        actionLabel: 'cameras.statusBanner.updateFirmware',
      };
      cameraBanners[key] = [firmwareUpdateBanner];
    }

    if ((this.isOwner || this.isOperator) && camera.software.updateInProgress) {
      const key = 'info-firmware-update-pending-on-details';
      const bannerId = key + '-' + camera.id;
      const firmwareUpdatePendingBanner: BannerConfiguration = {
        id: bannerId,
        severity: Severity.INFO,
        icon: 'firmware-update-installing',
        message: 'cameras.statusBanner.firmwareUpdating',
        cameraName: camera.name,
        postponable: false,
        clickHandler: () => this.store.dispatch(CamerasActions.closeFirmwareUpdatePendingBanner({ bannerId })),
        showCloseButton: false,
      };
      cameraBanners[key] = [firmwareUpdatePendingBanner];
    }

    if (this.instantModeActive) {
      const key = 'info-always-on-mode-active';
      const instantModeActiveBanner: BannerConfiguration = {
        id: key + '-' + camera.id,
        severity: Severity.INFO,
        icon: 'out-batteries-soon',
        message: 'cameras.details.statusBanner.alwaysOnModeActive.title',
        cameraName: camera.name,
        postponable: false,
        showCloseButton: false,
      };

      if (this.isOwner || this.isOperator) {
        const action = () => {
          this.store.dispatch(
            CamerasActions.openAlwaysOnModeDeactivationModal({
              cameraId: camera.id,
            })
          );
        };

        instantModeActiveBanner.clickHandler = action;
        instantModeActiveBanner.actionLabel = 'cameras.details.statusBanner.alwaysOnModeActive.deactivate';
        instantModeActiveBanner.actionHandler = action;
      } else {
        // User is Viewer
        const action = () => {
          this.store.dispatch(CamerasActions.openAlwaysOnModeInfoModal());
        };

        instantModeActiveBanner.clickHandler = action;
        instantModeActiveBanner.actionLabel = 'cameras.details.statusBanner.alwaysOnModeActive.learnMore';
        instantModeActiveBanner.actionHandler = action;
      }
      cameraBanners[key] = [instantModeActiveBanner];
    }
  }

  private collectBannersForCamera(camera: Camera): Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>> {
    this.isOwner = camera.permission === PermissionValues.OWNER;
    this.isOperator = camera.permission === PermissionValues.OPERATOR;
    this.isViewer = !this.isOwner && !this.isOperator;

    const cameraBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>> = {};

    this.addErrorLevelBanners(camera, cameraBanners);
    this.addWarningLevelBanners(camera, cameraBanners);
    this.addInfoLevelBanners(camera, cameraBanners);

    return cameraBanners;
  }

  private addCollectedBannersToOrdered(
    collectedBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>>,
    orderedBanners: CollectedStatusBanners<CameraDetailsStatusBannerTypes>
  ): void {
    Object.keys(collectedBanners).forEach((key: string) => {
      const typedKey = key as CameraDetailsStatusBannerTypes;
      if (!collectedBanners[typedKey]) {
        return;
      }

      orderedBanners[typedKey].push(...collectedBanners[typedKey]);
    });
  }

  public collect(camera: Camera, instantModeActive: boolean): Array<BannerConfiguration> {
    this.instantModeActive = instantModeActive;

    const orderedBanners: CollectedStatusBanners<CameraDetailsStatusBannerTypes> = {
      'error-low-battery-details': [],
      'warning-out-batteries-soon-details': [],
      'info-always-on-mode-active': [],
      'info-firmware-update-available-on-details': [],
      'info-firmware-update-pending-on-details': [],
    };

    const collectedBanners: Partial<CollectedStatusBanners<CameraDetailsStatusBannerTypes>> = this.collectBannersForCamera(camera);
    this.addCollectedBannersToOrdered(collectedBanners, orderedBanners);

    return Object.values(orderedBanners).flat();
  }
}
